package pl.decks.personal.pages.ievp.sections.pitch

import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.style.*
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.vars.animation.TransitionDurationVars
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.times
import org.jetbrains.compose.web.css.vh
import pl.decks.personal.extensions.toSitePalette
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.SiteLayout


object PitchSectionStylesheet {
    object Vars {
        val foldedSize by StyleVariable<CSSLengthOrPercentageNumericValue>()
        val stateChangeDuration by StyleVariable(TransitionDurationVars.VerySlow.value())
        val sectionAccentColor by StyleVariable<Color>()
        val PIBoxBackgroundColor by StyleVariable<Color>()
        val PIButtonBackgroundColor by StyleVariable<Color>()
        val PIButtonHoverColor by StyleVariable<Color>()
        val PIButtonPressedColor by StyleVariable<Color>()

        object Internal {
            val PICircleRounding by StyleVariable(SiteLayout.Rounding.Full)
            val PIBoxRounding by StyleVariable(SiteLayout.Rounding.XLarge)
            val PIBoxWidth by StyleVariable(SiteLayout.Fraction.All)
            val PIBoxMaxHeight by StyleVariable<CSSLengthOrPercentageNumericValue>()
        }
    }

    object PersonalIntro {
        val BoundingContainer = CssStyle.base {
            Modifier.minHeight(Vars.Internal.PIBoxMaxHeight.value()).fillMaxWidth()
        }

        sealed interface Kind : ComponentKind

        val Style = CssStyle.base<Kind> {
            Modifier
                .overflow(Overflow.Hidden)
                .border(
                    SiteLayout.Line.Thick,
                    LineStyle.Solid,
                    Vars.sectionAccentColor.value(colorMode.toPalette().border)
                )
        }
        val ActionableVariant = Style.addVariant {
            hover {
                Modifier.backgroundColor(Vars.PIButtonHoverColor.value(Colors.Transparent))
            }
            active {
                Modifier.backgroundColor(
                    Vars.PIButtonPressedColor.value(Vars.PIButtonHoverColor.value(Colors.Transparent))
                )
            }
        }

        val CircleVariant = Style.addVariantBase {
            Modifier
                .alignContent(AlignContent.Stretch)
                .backgroundColor(Vars.PIButtonBackgroundColor.value(Colors.Transparent))
                .borderRadius(Vars.Internal.PICircleRounding.value())
                .width(Vars.foldedSize.value())
                .height(Vars.foldedSize.value())
                .fontSize(11.cssRem)
        }
        val BoxVariant = Style.addVariantBase {
            Modifier
                .backgroundColor(Vars.PIBoxBackgroundColor.value(Colors.Transparent))
                .borderRadius(Vars.Internal.PIBoxRounding.value())
                .width(Vars.Internal.PIBoxWidth.value())
                .maxHeight(Vars.Internal.PIBoxMaxHeight.value())
                .fontSize(FontTypography.Size.Normal)
                .padding(SiteLayout.Space.Medium)
        }
    }

    object Revealed {
        val Text = FontTypography.mono.extendedByBase {
            Modifier
                .fillMaxWidth()
                .fontWeight(FontTypography.Weight.Medium)
                .color(Vars.sectionAccentColor.value(colorMode.toPalette().color))
        }
    }

    val ClickPrompt = FontTypography.sansSerif.extendedByBase {
        Modifier
            .fontSize(FontSize.Inherit)
            .color(colorMode.toPalette().background)
            .translateX(SiteLayout.Fraction.Quarter * 0.33)
            .transition {
                property("opacity")
                timingFunction(TransitionTimingFunction.EaseIn)
                duration(Vars.stateChangeDuration.value() * 0.5)
            }
    }
    val Section = CssStyle {
        val accent = colorMode.toSitePalette().accents.primary.toRgb()
        base {
            Modifier
                .minHeight(100.vh)
                .fillMaxWidth()
                .setVariable(Vars.foldedSize, 14.cssRem)
                .setVariable(Vars.Internal.PIBoxMaxHeight, Vars.foldedSize.value() * 2.5)
                .setVariable(Vars.sectionAccentColor, accent)
                .setVariable(Vars.PIButtonBackgroundColor, accent.copyf(alpha = 0.8f))
                .setVariable(Vars.PIButtonHoverColor, accent.copyf(alpha = 0.65f))
                .setVariable(Vars.PIButtonPressedColor, accent.copyf(alpha = 0.5f))
                .setVariable(Vars.PIBoxBackgroundColor, colorMode.toSitePalette().surface)
        }
        Breakpoint.MD {
            Modifier.padding(SiteLayout.Space.XXLarge).setVariable(Vars.foldedSize, 16.cssRem)
        }
    }
}
